import { useMemo, useRef, useEffect, useState } from 'react';
import useAdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/useAdvancedSearchContext';
import useProductVariantsToShow from 'hooks/useProductListingData/useProductVariantsToShow';
import useStaticProductsData from './useStaticProductsData';
import addDerivedProductData from '../useProductListingData/helpers/addDerivedProductData';

const useInfiniteStaticProducts = ({
  productIds: allProductIds,
  searchKey,
}) => {
  const fetchedIdsRef = useRef(new Set());
  const previousProductIdsRef = useRef([]);
  const previousProductIdsSignatureRef = useRef('');
  const previousSearchKeyRef = useRef(searchKey);

  const { selectedColors } = useAdvancedSearchContext();

  const [accumulatedProducts, setAccumulatedProducts] = useState([]);
  const [
    accumulatedUpholsteryOptions,
    setAccumulatedUpholsteryOptions,
  ] = useState([]);

  const upholsteryLabelFilters = useMemo(
    () => accumulatedUpholsteryOptions.map(option => option.label),
    [accumulatedUpholsteryOptions]
  );

  useEffect(() => {
    if (searchKey !== previousSearchKeyRef.current) {
      // When search term/context changes, clear all accumulated data
      // This ensures no products from the previous search appear in the new results
      fetchedIdsRef.current = new Set();
      setAccumulatedProducts([]);
      setAccumulatedUpholsteryOptions([]);
      previousProductIdsRef.current = [];
      previousProductIdsSignatureRef.current = '';
      previousSearchKeyRef.current = searchKey;
    }
  }, [searchKey]);

  useEffect(() => {
    const currentIds = allProductIds || [];
    const previousIds = previousProductIdsRef.current || [];

    const currentSignature = currentIds.slice(0, 5).join(',');
    const previousSignature = previousProductIdsSignatureRef.current;

    const firstIdChanged =
      previousIds.length > 0 &&
      currentIds.length > 0 &&
      String(previousIds[0]) !== String(currentIds[0]);

    const signatureChanged =
      previousSignature !== '' &&
      currentSignature !== '' &&
      previousSignature !== currentSignature &&
      !currentSignature.startsWith(previousSignature);

    const shouldReset = firstIdChanged || signatureChanged;

    // If Algolia returned completely different product IDs (e.g., filters changed significantly),
    // reset the accumulation to prevent showing stale products from the previous result set
    if (shouldReset) {
      fetchedIdsRef.current = new Set();
      setAccumulatedProducts([]);
      setAccumulatedUpholsteryOptions([]);
    }

    previousProductIdsRef.current = currentIds;
    previousProductIdsSignatureRef.current = currentSignature;
  }, [allProductIds]);

  const newProductIds = useMemo(() => {
    if (!allProductIds?.length) return [];

    const fetchedNormalized = Array.from(fetchedIdsRef.current).map(id =>
      String(id)
    );
    return allProductIds.filter(id => !fetchedNormalized.includes(String(id)));
  }, [allProductIds]);

  const shouldFetch = newProductIds.length > 0;

  const currentData = useStaticProductsData(
    {
      productIds: newProductIds,
    },
    {
      skip: !shouldFetch,
    }
  );

  const newProductIdsRef = useRef(newProductIds);
  newProductIdsRef.current = newProductIds;

  useEffect(() => {
    const products = currentData?.products;
    const upholsteryOptions = currentData?.upholsteryOptions;
    const isLoading = currentData?.loading;
    const newIds = newProductIdsRef.current;

    if (!products && !isLoading) return;

    if (products?.length > 0) {
      newIds.forEach(id => fetchedIdsRef.current.add(String(id)));

      setAccumulatedProducts(prev => {
        const existingIds = new Set(prev.map(p => String(p.id)));
        const newProducts = products.filter(
          p => !existingIds.has(String(p.id))
        );

        return [...prev, ...newProducts];
      });

      if (upholsteryOptions?.length > 0) {
        setAccumulatedUpholsteryOptions(prev => {
          // only accumulate upholstery option if its not already present
          const existingIds = new Set(prev.map(opt => opt.id));
          const newOptions = upholsteryOptions.filter(
            opt => !existingIds.has(opt.id)
          );

          return [...prev, ...newOptions];
        });
      }
    }
  }, [
    currentData?.products,
    currentData?.upholsteryOptions,
    currentData?.loading,
  ]);

  const finalProducts = useMemo(() => {
    if (!allProductIds?.length || !accumulatedProducts?.length) {
      return accumulatedProducts;
    }

    // Only return products that are in the current allProductIds list
    // This ensures no stale products from previous searches appear
    // when filter state changes and Algolia returns a new set of IDs
    const requestedIdsNormalized = new Set(allProductIds.map(id => String(id)));

    return accumulatedProducts.filter(product => {
      const productIdNormalized = String(product.id);
      return requestedIdsNormalized.has(productIdNormalized);
    });
  }, [accumulatedProducts, allProductIds]);

  const productsWithAdditionalData = useMemo(
    () =>
      finalProducts.map(product =>
        addDerivedProductData({
          product,
          upholsteryOptions: accumulatedUpholsteryOptions,
        })
      ),
    [accumulatedUpholsteryOptions, finalProducts]
  );

  const colorFiltersArray = useMemo(
    () => Object.keys(selectedColors).filter(color => selectedColors[color]),
    [selectedColors]
  );

  const productsWithVariantsToShow = useProductVariantsToShow({
    colorFilters: colorFiltersArray.length > 0 ? colorFiltersArray : undefined,
    products: productsWithAdditionalData,
    upholsteryLabelFilters,
  });

  return {
    products: productsWithVariantsToShow ?? [],
    loading: currentData?.loading || false,
  };
};

export default useInfiniteStaticProducts;
